import { useLanguage } from '@/i18n/LanguageContext';
import { motion } from 'framer-motion';
import { BookOpen, ArrowLeft, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePostsPreview, getTranslation } from '@/hooks/useSupabaseData';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';
import bookCover from '@/assets/book-cover.jpeg';

type PostTranslation = { lang: string; title: string; excerpt: string };

const BookPreviewSection = () => {
  const { t, lang } = useLanguage();
  const { data: posts } = usePostsPreview();
  const isRtl = lang !== 'en';
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const chapters = posts ?? [];

  const updateScrollButtons = () => {
    // arrows always active
  };

  useEffect(() => {
    updateScrollButtons();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollButtons, { passive: true });
      return () => el.removeEventListener('scroll', updateScrollButtons);
    }
  }, [chapters.length]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = 280;
    // In visual terms: 'right' means forward (towards end), 'left' means backward
    // For RTL containers, scrollLeft is 0 at the right edge and negative going left
    const isAtStart = el.scrollLeft <= 0 && !isRtl || (isRtl && el.scrollLeft >= 0);
    
    if (dir === 'right') {
      const maxScroll = el.scrollWidth - el.clientWidth;
      const currentPos = Math.abs(el.scrollLeft);
      if (currentPos >= maxScroll - 10) {
        // At end, loop to start
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: isRtl ? -amount : amount, behavior: 'smooth' });
      }
    } else {
      const currentPos = Math.abs(el.scrollLeft);
      if (currentPos < 10) {
        // At start, loop to end
        const maxScroll = el.scrollWidth - el.clientWidth;
        el.scrollTo({ left: isRtl ? -maxScroll : maxScroll, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: isRtl ? amount : -amount, behavior: 'smooth' });
      }
    }
  };

  if (!posts?.length) return null;

  const Arrow = isRtl ? ArrowLeft : ArrowRight;

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,hsl(var(--primary)/0.05),transparent_60%)]" />
      <div className="container mx-auto px-4 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-4">
            <BookOpen className="w-4 h-4 text-primary" />
            <span className="text-sm text-primary font-medium">{t('book.title')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gradient-gold mb-3">{t('book.title')}</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">{t('book.subtitle')}</p>
        </motion.div>

        {/* Chapters strip with cover overlaid on top */}
        <div className="relative">
          {/* Full-width chapters horizontal scroll strip */}
          <div className="relative group pt-4">
            {/* Always-visible scroll arrows at section edges */}
            <button
              onClick={() => scroll('left')}
              aria-label="Scroll left"
              className="absolute top-1/2 -translate-y-1/2 start-0 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full bg-background border-2 border-primary/30 shadow-xl flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all -translate-x-1/2"
            >
              {isRtl ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </button>
            <button
              onClick={() => scroll('right')}
              aria-label="Scroll right"
              className="absolute top-1/2 -translate-y-1/2 end-0 z-30 w-11 h-11 md:w-12 md:h-12 rounded-full bg-background border-2 border-primary/30 shadow-xl flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-all translate-x-1/2"
            >
              {isRtl ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>

            <div className="absolute top-0 bottom-0 start-0 w-8 bg-gradient-to-e from-background to-transparent z-[5] pointer-events-none" />
            <div className="absolute top-0 bottom-0 end-0 w-8 bg-gradient-to-s from-background to-transparent z-[5] pointer-events-none" />

            <div
              ref={scrollRef}
              className="flex gap-4 overflow-x-auto scrollbar-none pb-4 snap-x snap-mandatory"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {chapters.map((post, i) => {
                const tr = getTranslation<PostTranslation>(post.post_translations, lang);
                if (!tr) return null;
                return (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05, duration: 0.4 }}
                    className="flex-shrink-0 w-[140px] md:w-[160px] snap-start"
                  >
                    <Link to={`/${lang}/blog/${post.slug}`} className="block group/card">
                      <motion.div
                        whileHover={{ scale: 1.05, y: -4 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                        className="relative rounded-xl overflow-hidden aspect-[3/4] mb-2 bg-secondary/20 shadow-md group-hover/card:shadow-xl transition-shadow duration-300"
                      >
                        {post.image_url ? (
                          <img src={post.image_url} alt={tr.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-secondary/30">
                            <BookOpen className="w-8 h-8 text-muted-foreground/30" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                        <div className="absolute bottom-2 start-2 end-2">
                          <span className="text-[9px] font-medium text-primary bg-primary/10 backdrop-blur-sm border border-primary/20 px-1.5 py-0.5 rounded-full">
                            {t('book.chapter')} {i + 1}
                          </span>
                        </div>
                      </motion.div>
                      <h3 className="font-bold text-[11px] md:text-xs text-foreground group-hover/card:text-primary transition-colors line-clamp-2 px-0.5">
                        {tr.title}
                      </h3>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>

        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <Link to={`/${lang}/blog`}>
            <Button size="lg" className="gap-2 bg-primary hover:bg-primary/90">
              {t('book.read_full')}
              <Arrow className="w-4 h-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default BookPreviewSection;
